import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarClock,
  User,
  MapPin,
  FileCheck,
  UserRound,
  Mail,
  Phone,
  Home,
  FileText,
  List,
  Star,
  CreditCard,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@shared/ui/avatar";
import { useConsumerDetails } from "../../hooks";
import { formatTerritoryAddress } from "@shared/utils/address";
import QuickActions from "@features/cx/consumerOnboarding/components/details/consumerInfo/quickActions";
import { NoteDialog } from "@features/cx/consumerOnboarding/components/details/addNotes";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { EditApplicationDialog } from "@features/cx/consumerOnboarding/components/details/editConsumerInfo";
import MaskedSSN from "@features/cx/shared/components/MaskedSSN";
export type ApplicationStatus =
  | "pending_review"
  | "document_verification"
  | "site_inspection"
  | "approval"
  | "approved"
  | "installation"
  | "accepted"
  | "approval_inprogress"
  | "survey_inprogress"
  | "survey_completed"
  | "installation_inprogress"
  | "installation_completed"
  | "activation"
  | "completed"
  | "rejected";

interface ApplicationHeaderProps {
  remote_utility_id: any;
  is_detail_info: any;
  daysRemaining?: number;
  totalDays?: number;
}

const getStatusBadge = (status: ApplicationStatus) => {
  switch (status) {
    case "pending_review":
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-700 border-blue-200"
        >
          Pending Review
        </Badge>
      );
    case "document_verification":
      return (
        <Badge
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200"
        >
          Document Verification
        </Badge>
      );
    case "site_inspection":
      return (
        <Badge
          variant="outline"
          className="bg-orange-50 text-orange-700 border-orange-200"
        >
          Site Inspection
        </Badge>
      );
    case "approval":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200"
        >
          Approved
        </Badge>
      );
    case "approved":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200"
        >
          Approved
        </Badge>
      );
    case "installation":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Scheduled
        </Badge>
      );
    case "accepted":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Accepted
        </Badge>
      );
    case "approval_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Approval Inprogress
        </Badge>
      );
    case "survey_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Survey Inprogress
        </Badge>
      );
    case "survey_completed":
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-700 border-teal-200"
        >
          Survey Completed
        </Badge>
      );
    case "installation_inprogress":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Inprogress
        </Badge>
      );
    case "installation_completed":
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-700 border-indigo-200"
        >
          Installation Completed
        </Badge>
      );
    case "completed":
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          Completed
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200"
        >
          Rejected
        </Badge>
      );
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

// Helper function to map API status to our ApplicationStatus
const mapApiStatusToApplicationStatus = (
  statusDisplay: string,
): ApplicationStatus => {
  switch (statusDisplay?.toUpperCase()) {
    case "APPROVED":
      return "approved";
    case "ACCEPTED":
      return "accepted";
    case "PENDING":
      return "pending_review";
    case "REJECTED":
      return "rejected";
    case "COMPLETED":
      return "completed";
    case "ACTIVATED":
      return "activation";
    case "APPROVAL INPROGRESS":
      return "approval_inprogress";
    case "SURVEY INPROGRESS":
      return "survey_inprogress";
    case "SURVEY COMPLETED":
      return "survey_completed";
    case "INSTALLATION INPROGRESS":
      return "installation_inprogress";
    default:
      return "pending_review";
  }
};

type Field = {
  label: string;
  value: string | React.ReactNode;
};
type DetailsSectionProps = {
  title: string;
  fields: Field[];
  columns?: number; // optional for layout control
};

export const DetailsSection = ({
  title,
  fields,
  columns = 4,
}: DetailsSectionProps) => {
  return (
    <div className="mb-6">
      {/* Section Title */}
      <div className="text-sm font-semibold tracking-wider text-gray-500 mb-2">
        {title}
      </div>

      {/* Card */}
      <div className="border border-gray-200 rounded-lg bg-white p-5">
        <div
          className={`grid gap-y-5 gap-x-10 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-${columns}`}
        >
          {fields.map((field, index) => (
            <div key={index}>
              <ValueLabel label={field.label} value={field.value} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ApplicationHeader = ({
  remote_utility_id,
  is_detail_info,
  daysRemaining = 0,
  totalDays = 0,
}: ApplicationHeaderProps) => {
  // Get consumer_id from URL parameters
  const { consumer_id, consumerId, id } = useParams<{
    consumer_id?: string;
    consumerId?: string;
    id?: string;
  }>();

  // Use the first available parameter (handles different URL patterns)
  const consumerIdFromUrl = consumer_id || consumerId || id;

  const {
    data: consumerData,
    refetch,
    isLoading,
    error,
  } = useConsumerDetails({
    remote_utility_id,
    consumer_id: consumerIdFromUrl || "",
    is_detail_info: true,
  });

  // Show error if no consumer ID is found in URL
  if (!consumerIdFromUrl) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-red-600">Error: Consumer ID not found in URL</div>
      </div>
    );
  }
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isCommunicationOpen, setIsCommunicationOpen] = useState(false);
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-red-600">
          Error loading consumer details: {error.message}
        </div>
      </div>
    );
  }

  if (!consumerData?.result) {
    return (
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="text-gray-600">No consumer data available</div>
      </div>
    );
  }

  const consumer = consumerData.result;
  console.log("debug consumer,", consumer);
  // Map API data to component props - keeping original prop names
  const customerName = `${consumer.firstName} ${consumer.lastName}`;
  const customerEmail = consumer.email;
  const customerPhone = consumer.contactNumber;
  const applicationId = consumer.consumerNo;
  const submissionDate = consumer.createdDate;

  // Combine all service address data - prioritize address_data, fallback to territory_data
  const serviceAddressData = consumer.addressData?.service;
  const serviceTerritoryData = consumer.territoryData?.service;
  console.log("debug addresss", serviceAddressData);
  const serviceAddress =
    formatTerritoryAddress(consumer.territoryData, "service") ||
    "No address configured";
  const billingAddress =
    formatTerritoryAddress(consumer.territoryData, "billing") ||
    "No address configured";

  console.log("debug serviceAddress", serviceAddressData?.region);

  // Combine all billing address data - prioritize address_data, fallback to territory_data
  const billingAddressData = consumer.addressData?.billing;
  const billingTerritoryData = consumer.territoryData?.billing;

  const plan = consumer.planDetails?.planName ?? "NA";
  const ssnEncrypted = consumer?.ssnEncrypted;
  const dateOfBirth = consumer?.dateOfBirth || "NA";
  const category = consumer.categoryDisplay;
  const subCategory = consumer.subCategoryDisplay;
  const status = mapApiStatusToApplicationStatus(consumer.statusDisplay);
  const createdBy = consumer.createdUserRemoteName || "";
  const updatedOn = consumer.lastModifiedDate;
  const lastUpdatedBy = consumer.lastModifiedUserRemoteName || "";
  const progressStatus = consumer.progressStatusDisplay;
  const contactFields = [
    {
      label: "Email",
      value: customerEmail ? (
        <div
          title={customerEmail}
          className="truncate max-w-[180px] lg:max-w-full"
        >
          {customerEmail}
        </div>
      ) : (
        "N/A"
      ),
    },
    { label: "Phone", value: customerPhone || "N/A" },
    { label: "Service Address", value: serviceAddress },
    { label: "Mailing Address", value: billingAddress },
    { label: "Category", value: category },
    { label: "Sub Category", value: subCategory },
    { label: "SSN", value: <MaskedSSN ssn={ssnEncrypted} /> },
    { label: "Date of birth", value: dateOfBirth },
  ];

  const applicationDetailsFields = [
    { label: "Created On", value: submissionDate || "N/A" },
    { label: "Created By", value: createdBy || "N/A" },
    { label: "Updated On", value: updatedOn || "N/A" },
    { label: "Last Updated By", value: lastUpdatedBy || "N/A" },
    { label: "Accepted On", value: consumer?.acceptedDate || "N/A" },
    { label: "Accepted By", value: consumer?.acceptedBy || "N/A" },
    { label: "Approve On", value: consumer?.approvedDate || "N/A" },
    { label: "Approve By", value: consumer?.approvedBy || "N/A" },
  ];

  const slaAchievedFields = [
    {
      label: "Acknowledgement Time",
      value: (
        <span className="text-green-600 font-semibold text-lg">
          {consumer?.slaInfo?.acknowledgeTimeHours ?? "NA"}
        </span>
      ),
    },
    {
      label: "Response Time",
      value: (
        <span className="text-green-600 font-semibold text-lg">
          {consumer?.slaInfo?.responseTimeHours ?? "NA"}
        </span>
      ),
    },
    {
      label: "Resolution Time",
      value: (
        <span className="text-orange-500 font-semibold text-lg">
          {consumer?.slaInfo?.resolutionTimeHours ?? "NA"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-workflow-blue">
              <AvatarImage src="/placeholder.svg" alt={customerName} />
              <AvatarFallback>{customerName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold flex items-center gap-2">
                  {customerName}{" "}
                  <p className=" text-xl font-bold">#{applicationId}</p>
                </h1>
              </div>
              {getStatusBadge(status)}
              <Badge
                className={`ml-2 bg-blue-100 text-blue-700 text-xs border border-blue-300`}
              >
                {progressStatus}
              </Badge>
              {/* Tags Section */}
              {consumer.tags && consumer.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {consumer.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-gray-100 text-gray-700 border-gray-300 text-xs"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-md">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">SLA Status</p>
              <p className="text-sm font-medium flex items-center justify-center">
                <span>{consumer.slaInfo?.slaStatus || "N/A"}</span>
              </p>
            </div>
            <div className="text-center border-x border-gray-200 px-2">
              <p className="text-sm text-muted-foreground">SLA Due Date</p>
              <p className="text-sm font-medium">
                {consumer.slaInfo?.slaDueDate || "N/A"}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Time Remaining</p>
              <p className="text-sm font-medium">
                {consumer.slaInfo?.slaTimeRemaining || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 border rounded-lg p-4">
          <DetailsSection
            title="Account Details"
            fields={contactFields}
            columns={4}
          />
          <DetailsSection
            title="Audit Details"
            fields={applicationDetailsFields}
            columns={4}
          />
        </div>

        <div className="lg:col-span-1">
          <QuickActions
            consumerData={consumerData}
            onRefetch={refetch}
            onAddNote={() => setIsNoteOpen(true)}
            onSendCommunication={() => setIsCommunicationOpen(true)}
          />
        </div>
      </div>
      <NoteDialog
        isOpen={isNoteOpen}
        onClose={() => setIsNoteOpen(false)}
        itemId="123" // replace with actual item id
        source="consumer"
        title={customerName}
      />
      {/* Communication Dialog */}
      <CommunicationDialog
        isOpen={isCommunicationOpen}
        onClose={() => setIsCommunicationOpen(false)}
        consumerId="123"
        remoteUtilityId={1}
        email="john.doe@example.com"
        phone="1234567890"
        communicationType="UPDATE"
        useChannelType={() => {}}
        useChannelTemplates={() => {}}
        useSendCommunication={() => ({ mutateAsync: async () => {} })}
      />
    </>
  );
};

export default ApplicationHeader;
